import { useQuery } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Clock3,
  Heart,
  MailOpen,
  MapPin,
  X,
  ZoomIn,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LoadingState } from "../../components/Ui";
import { getWeddingInvitation } from "../../lib/api";
import { applyPageMetadata } from "../../lib/seo";
import type { WeddingInvitation } from "../../types/content";
import "./wedding.css";

function useWeddingMetadata(path: string, image = "/wedding/v1/wedding-hero-hijab.jpg") {
  useEffect(() => {
    applyPageMetadata({
      title: "Contoh Undangan Pernikahan | Steria House",
      description:
        "Contoh undangan pernikahan digital yang dibuat oleh Steria House.",
      canonicalPath: path,
      image,
      imageAlt: "Contoh undangan pernikahan digital Steria House",
      noindex: true,
    });
    document.documentElement.lang = "id";
  }, [image, path]);
}

export function WeddingStudioPage() {
  useWeddingMetadata("/wedding");

  return (
    <main className="wedding-studio">
      <div className="wedding-studio-grain" aria-hidden="true" />
      <header className="wedding-studio-nav">
        <Link to="/" aria-label="Kembali ke Steria House">
          <ArrowLeft size={17} />
          Steria House
        </Link>
        <span>Pengalaman Pernikahan</span>
      </header>
      <section className="wedding-studio-hero">
        <div className="wedding-studio-copy">
          <span className="wedding-kicker">Koleksi tersembunyi · 02</span>
          <h1>Kisah yang indah, dirangkai menjadi pengalaman digital.</h1>
          <p>
            Sebuah contoh undangan pernikahan yang personal, hangat, dan dapat
            disesuaikan untuk setiap pasangan maupun tamu.
          </p>
          <Link
            className="wedding-studio-button"
            to="/wedding/v2?m=Arga&f=Kirana&kepada=Tamu%20Undangan"
          >
            Lihat template terbaru <ArrowUpRight size={18} />
          </Link>
        </div>
        <div className="wedding-studio-collection">
          <Link
            className="wedding-studio-preview"
            to="/wedding/v1?m=Raka&f=Nadira&kepada=Tamu%20Undangan"
            aria-label="Buka undangan contoh Nadira dan Raka"
          >
            <img
              src="/wedding/v1/wedding-hero-hijab.jpg"
              alt="Contoh undangan pernikahan Nadira dan Raka"
            />
            <div>
              <span>Template 01</span>
              <strong>Nadira &amp; Raka</strong>
              <i>Janji di taman</i>
            </div>
          </Link>
          <Link
            className="wedding-studio-preview wedding-studio-preview-v2"
            to="/wedding/v2?m=Arga&f=Kirana&kepada=Tamu%20Undangan"
            aria-label="Buka undangan contoh Kirana dan Arga"
          >
            <img
              src="/wedding/v1/wedding-walk-hijab.jpg"
              alt="Contoh undangan pernikahan Kirana dan Arga"
            />
            <div>
              <span>Template 02</span>
              <strong>Kirana &amp; Arga</strong>
              <i>Editorial modern</i>
            </div>
          </Link>
        </div>
      </section>
      <footer className="wedding-studio-footer">
        <span>Undangan digital oleh Steria House</span>
        <span>Jakarta · Indonesia</span>
      </footer>
    </main>
  );
}

type Countdown = {
  hari: string;
  jam: string;
  menit: string;
  detik: string;
};

function getCountdown(eventDate: string): Countdown {
  const distance = Math.max(0, new Date(eventDate).getTime() - Date.now());
  const day = 86_400_000;
  const hour = 3_600_000;
  const minute = 60_000;
  return {
    hari: String(Math.floor(distance / day)).padStart(2, "0"),
    jam: String(Math.floor((distance % day) / hour)).padStart(2, "0"),
    menit: String(Math.floor((distance % hour) / minute)).padStart(2, "0"),
    detik: String(Math.floor((distance % minute) / 1000)).padStart(2, "0"),
  };
}

function cleanRecipient(value: string | null) {
  const normalized = (value || "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return normalized || "Tamu Undangan";
}

function cleanDisplayName(value: string | null, fallback: string) {
  const normalized = (value || "")
    .replace(/[<>{}\\]/g, "")
    .replace(/\p{Cc}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);
  return normalized || fallback;
}

function safeExternalURL(value: string, fallback: string) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : fallback;
  } catch {
    return fallback;
  }
}

function InvitationContent({
  invitation,
  recipient,
}: {
  invitation: WeddingInvitation;
  recipient: string;
}) {
  const [opened, setOpened] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(() =>
    getCountdown(invitation.eventDate),
  );
  const mapsURL = safeExternalURL(
    invitation.mapsUrl,
    "https://maps.google.com/",
  );
  const whatsapp = invitation.contactWhatsApp.replace(/\D/g, "");

  useEffect(() => {
    document.body.classList.toggle("wedding-cover-locked", !opened);
    return () => document.body.classList.remove("wedding-cover-locked");
  }, [opened]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setCountdown(getCountdown(invitation.eventDate)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [invitation.eventDate]);

  useEffect(() => {
    if (!opened) return;
    const elements = document.querySelectorAll<HTMLElement>(
      "[data-wedding-reveal]",
    );
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [opened]);

  useEffect(() => {
    if (lightbox === null) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowRight") {
        setLightbox((current) =>
          current === null ? null : (current + 1) % invitation.gallery.length,
        );
      }
      if (event.key === "ArrowLeft") {
        setLightbox((current) =>
          current === null
            ? null
            : (current - 1 + invitation.gallery.length) %
              invitation.gallery.length,
        );
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [invitation.gallery.length, lightbox]);

  const pageStyle = {
    "--wedding-accent": invitation.accentColor || "#6f7858",
    "--wedding-paper": invitation.backgroundColor || "#f7f3eb",
  } as CSSProperties;

  return (
    <main className={`wedding-invitation ${opened ? "is-opened" : ""}`} style={pageStyle}>
      <section
        className={`wedding-cover ${opened ? "is-opening" : ""}`}
        aria-hidden={opened}
      >
        <img src={invitation.heroImageUrl} alt="" />
        <div className="wedding-cover-shade" />
        <div className="wedding-cover-flourish" aria-hidden="true">
          <i />
          <Heart size={14} />
          <i />
        </div>
        <div className="wedding-cover-content">
          <span>Pernikahan</span>
          <h1>
            <span>{invitation.brideName}</span>
            <i>&amp;</i>
            <span>{invitation.groomName}</span>
          </h1>
          <p>Kepada Yth.</p>
          <strong>{recipient}</strong>
          <small>Mohon maaf apabila ada kesalahan penulisan nama atau gelar.</small>
          <button
            type="button"
            onClick={() => {
              setOpened(true);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
          >
            <MailOpen size={17} />
            Buka Undangan
          </button>
        </div>
      </section>

      <div className="wedding-page">
        <header className="wedding-hero">
          <img src={invitation.heroImageUrl} alt={`${invitation.brideName} dan ${invitation.groomName}`} />
          <div className="wedding-hero-overlay" />
          <div className="wedding-petals" aria-hidden="true">
            {Array.from({ length: 9 }, (_, index) => (
              <i
                key={index}
                style={
                  {
                    "--petal-left": `${8 + index * 10.5}%`,
                    "--petal-duration": `${10 + index * 0.75}s`,
                    "--petal-delay": `${index * -1.35}s`,
                    "--petal-opacity": 0.26 + index * 0.025,
                    "--petal-rotation": `${index * 31}deg`,
                  } as CSSProperties
                }
              />
            ))}
          </div>
          <div className="wedding-hero-copy">
            <span data-wedding-reveal>Pernikahan</span>
            <h1 data-wedding-reveal>
              <span>{invitation.brideName}</span>
              <i>&amp;</i>
              <span>{invitation.groomName}</span>
            </h1>
            <p data-wedding-reveal>{invitation.eventDateLabel}</p>
          </div>
          <a className="wedding-scroll-cue" href="#wedding-welcome">
            <span>Jelajahi kisah kami</span>
            <ArrowDown size={17} />
          </a>
        </header>

        <section className="wedding-welcome" id="wedding-welcome">
          <div className="wedding-ornament" data-wedding-reveal aria-hidden="true">
            <i />
            <Heart size={15} />
            <i />
          </div>
          <p className="wedding-quote" data-wedding-reveal>
            “{invitation.openingQuote}”
          </p>
          <span className="wedding-quote-source" data-wedding-reveal>
            QS. Ar-Rum: 21
          </span>
          <p className="wedding-opening" data-wedding-reveal>
            {invitation.openingText}
          </p>
          <div className="wedding-couple">
            <article data-wedding-reveal>
              <span>Mempelai wanita</span>
              <h2>{invitation.brideFullName}</h2>
              <p>{invitation.brideParents}</p>
            </article>
            <b data-wedding-reveal>&amp;</b>
            <article data-wedding-reveal>
              <span>Mempelai pria</span>
              <h2>{invitation.groomFullName}</h2>
              <p>{invitation.groomParents}</p>
            </article>
          </div>
        </section>

        <section className="wedding-date">
          <div className="wedding-date-image" data-wedding-reveal>
            <img
              src={invitation.gallery[3]?.imageUrl || invitation.heroImageUrl}
              alt={`${invitation.brideName} dan ${invitation.groomName} berjalan bersama`}
              loading="lazy"
            />
          </div>
          <div className="wedding-date-content">
            <span className="wedding-section-kicker" data-wedding-reveal>
              Catat tanggalnya
            </span>
            <h2 data-wedding-reveal>{invitation.eventDateLabel}</h2>
            <div className="wedding-countdown" data-wedding-reveal>
              {Object.entries(countdown).map(([label, value]) => (
                <div key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
            <div className="wedding-events">
              <article data-wedding-reveal>
                <CalendarDays size={21} />
                <span>Akad Nikah</span>
                <strong>{invitation.ceremonyTime}</strong>
              </article>
              <article data-wedding-reveal>
                <Clock3 size={21} />
                <span>Resepsi</span>
                <strong>{invitation.receptionTime}</strong>
              </article>
            </div>
            <div className="wedding-location" data-wedding-reveal>
              <MapPin size={23} />
              <div>
                <strong>{invitation.venueName}</strong>
                <p>{invitation.venueAddress}</p>
              </div>
            </div>
            <a
              className="wedding-outline-button"
              href={mapsURL}
              target="_blank"
              rel="noreferrer"
              data-wedding-reveal
            >
              Buka Google Maps <ArrowUpRight size={17} />
            </a>
          </div>
        </section>

        <section className="wedding-story">
          <span className="wedding-section-kicker" data-wedding-reveal>
            Perjalanan kami
          </span>
          <h2 data-wedding-reveal>{invitation.storyTitle}</h2>
          <div className="wedding-story-line">
            {invitation.story.map((item, index) => (
              <article key={`${item.period}-${index}`} data-wedding-reveal>
                <span>{item.period}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="wedding-gallery">
          <span className="wedding-section-kicker" data-wedding-reveal>
            Galeri
          </span>
          <h2 data-wedding-reveal>{invitation.galleryTitle}</h2>
          <div className="wedding-gallery-grid">
            {invitation.gallery.map((image, index) => (
              <button
                type="button"
                key={`${image.imageUrl}-${index}`}
                onClick={() => setLightbox(index)}
                data-wedding-reveal
                aria-label={`Perbesar foto: ${image.altText}`}
              >
                <img src={image.imageUrl} alt={image.altText} loading="lazy" />
                <span>
                  <ZoomIn size={18} /> Lihat foto
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="wedding-closing">
          <div className="wedding-closing-photo" data-wedding-reveal>
            <img
              src={invitation.gallery[1]?.imageUrl || invitation.heroImageUrl}
              alt="Cincin pernikahan dan bunga melati"
              loading="lazy"
            />
          </div>
          <div className="wedding-closing-copy">
            <span className="wedding-section-kicker" data-wedding-reveal>
              Sampai bertemu
            </span>
            <h2 data-wedding-reveal>{invitation.closingTitle}</h2>
            <p data-wedding-reveal>{invitation.closingMessage}</p>
            {whatsapp && (
              <a
                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(
                  `Halo ${invitation.brideName} dan ${invitation.groomName}, saya ${recipient} ingin mengonfirmasi kehadiran.`,
                )}`}
                target="_blank"
                rel="noreferrer"
                className="wedding-solid-button"
                data-wedding-reveal
              >
                Konfirmasi Kehadiran <ArrowUpRight size={17} />
              </a>
            )}
            <strong data-wedding-reveal>
              <span>{invitation.brideName}</span>
              <i>&amp;</i>
              <span>{invitation.groomName}</span>
            </strong>
          </div>
        </section>

        <footer className="wedding-footer">
          <Heart size={14} fill="currentColor" />
          <span>Steria House</span>
          <i aria-hidden="true">•</i>
          <span>Undangan Digital</span>
        </footer>
      </div>

      {lightbox !== null && invitation.gallery[lightbox] && (
        <div
          className="wedding-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Galeri foto pernikahan"
          onClick={() => setLightbox(null)}
        >
          <button type="button" onClick={() => setLightbox(null)} aria-label="Tutup galeri">
            <X size={22} />
          </button>
          <img
            src={invitation.gallery[lightbox].imageUrl}
            alt={invitation.gallery[lightbox].altText}
            onClick={(event) => event.stopPropagation()}
          />
          <span>
            {lightbox + 1} / {invitation.gallery.length}
          </span>
        </div>
      )}
    </main>
  );
}

function InvitationContentV2({
  invitation,
  recipient,
}: {
  invitation: WeddingInvitation;
  recipient: string;
}) {
  const [opened, setOpened] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(() =>
    getCountdown(invitation.eventDate),
  );
  const mapsURL = safeExternalURL(
    invitation.mapsUrl,
    "https://maps.google.com/",
  );
  const whatsapp = invitation.contactWhatsApp.replace(/\D/g, "");
  const pageStyle = {
    "--wedding-v2-accent": invitation.accentColor || "#9a5f54",
    "--wedding-v2-paper": invitation.backgroundColor || "#f3ece4",
  } as CSSProperties;

  useEffect(() => {
    document.body.classList.toggle("wedding-cover-locked", !opened);
    return () => document.body.classList.remove("wedding-cover-locked");
  }, [opened]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setCountdown(getCountdown(invitation.eventDate)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [invitation.eventDate]);

  useEffect(() => {
    if (!opened) return;
    const elements = document.querySelectorAll<HTMLElement>(
      ".wedding-v2 [data-wedding-v2-reveal]",
    );
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -7% 0px" },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [opened]);

  useEffect(() => {
    if (lightbox === null) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowRight") {
        setLightbox((current) =>
          current === null ? null : (current + 1) % invitation.gallery.length,
        );
      }
      if (event.key === "ArrowLeft") {
        setLightbox((current) =>
          current === null
            ? null
            : (current - 1 + invitation.gallery.length) %
              invitation.gallery.length,
        );
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [invitation.gallery.length, lightbox]);

  return (
    <main
      className={`wedding-v2 ${opened ? "is-opened" : ""}`}
      style={pageStyle}
    >
      <section
        className={`wedding-v2-cover ${opened ? "is-opening" : ""}`}
        aria-hidden={opened}
      >
        <div className="wedding-v2-cover-photo">
          <img src={invitation.heroImageUrl} alt="" />
          <span>02 / Steria Wedding</span>
        </div>
        <div className="wedding-v2-cover-panel">
          <div className="wedding-v2-monogram" aria-hidden="true">
            {invitation.brideName.slice(0, 1)}
            <i>&amp;</i>
            {invitation.groomName.slice(0, 1)}
          </div>
          <p>Undangan pernikahan</p>
          <h1>
            <span>{invitation.brideName}</span>
            <i>&amp;</i>
            <span>{invitation.groomName}</span>
          </h1>
          <div className="wedding-v2-recipient">
            <small>Kepada Yth.</small>
            <strong>{recipient}</strong>
          </div>
          <button
            type="button"
            onClick={() => {
              setOpened(true);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
          >
            Buka Undangan <ArrowUpRight size={17} />
          </button>
        </div>
      </section>

      <div className="wedding-v2-page">
        <header className="wedding-v2-hero">
          <nav>
            <span>
              {invitation.brideName.slice(0, 1)}
              <i>&amp;</i>
              {invitation.groomName.slice(0, 1)}
            </span>
            <p>{invitation.eventDateLabel}</p>
            <a href="#wedding-v2-acara">Detail acara</a>
          </nav>
          <div className="wedding-v2-hero-copy">
            <span data-wedding-v2-reveal>Kami akan menikah</span>
            <h1 data-wedding-v2-reveal>
              <strong>{invitation.brideName}</strong>
              <i>&amp;</i>
              <strong>{invitation.groomName}</strong>
            </h1>
            <p data-wedding-v2-reveal>
              Dua perjalanan. Satu rumah. Sebuah cerita yang baru dimulai.
            </p>
          </div>
          <figure data-wedding-v2-reveal>
            <img
              src={invitation.heroImageUrl}
              alt={`${invitation.brideName} dan ${invitation.groomName}`}
            />
            <figcaption>
              <span>Bandung, Indonesia</span>
              <span>November · 2027</span>
            </figcaption>
          </figure>
          <div className="wedding-v2-orbit" aria-hidden="true">
            <Heart size={16} fill="currentColor" />
          </div>
        </header>

        <section className="wedding-v2-intro">
          <div data-wedding-v2-reveal>
            <span className="wedding-v2-label">Tentang perjalanan</span>
            <p>“{invitation.openingQuote}”</p>
          </div>
          <article data-wedding-v2-reveal>
            <span>Dengan penuh syukur</span>
            <p>{invitation.openingText}</p>
          </article>
        </section>

        <section className="wedding-v2-couple">
          <div className="wedding-v2-couple-heading" data-wedding-v2-reveal>
            <span>Yang berbahagia</span>
            <h2>Dua nama, satu tujuan.</h2>
          </div>
          <div className="wedding-v2-couple-grid">
            <article data-wedding-v2-reveal>
              <span>01 · Mempelai wanita</span>
              <h3>{invitation.brideFullName}</h3>
              <p>{invitation.brideParents}</p>
            </article>
            <i data-wedding-v2-reveal>&amp;</i>
            <article data-wedding-v2-reveal>
              <span>02 · Mempelai pria</span>
              <h3>{invitation.groomFullName}</h3>
              <p>{invitation.groomParents}</p>
            </article>
          </div>
        </section>

        <section className="wedding-v2-event" id="wedding-v2-acara">
          <div className="wedding-v2-event-photo">
            <img
              src={invitation.gallery[1]?.imageUrl || invitation.heroImageUrl}
              alt={invitation.gallery[1]?.altText || "Lokasi acara pernikahan"}
              loading="lazy"
            />
            <div className="wedding-v2-event-photo-copy" data-wedding-v2-reveal>
              <span>Catat tanggalnya</span>
              <h2>{invitation.eventDateLabel}</h2>
            </div>
          </div>
          <div className="wedding-v2-event-panel">
            <div className="wedding-v2-countdown" data-wedding-v2-reveal>
              {Object.entries(countdown).map(([label, value]) => (
                <div key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
            <div className="wedding-v2-event-list">
              <article data-wedding-v2-reveal>
                <span>Akad nikah</span>
                <strong>{invitation.ceremonyTime}</strong>
                <CalendarDays size={19} />
              </article>
              <article data-wedding-v2-reveal>
                <span>Resepsi</span>
                <strong>{invitation.receptionTime}</strong>
                <Clock3 size={19} />
              </article>
            </div>
            <div className="wedding-v2-venue" data-wedding-v2-reveal>
              <span>Lokasi</span>
              <h3>{invitation.venueName}</h3>
              <p>{invitation.venueAddress}</p>
              <a href={mapsURL} target="_blank" rel="noreferrer">
                Lihat peta <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </section>

        <section className="wedding-v2-story">
          <header data-wedding-v2-reveal>
            <span className="wedding-v2-label">Perjalanan kami</span>
            <h2>{invitation.storyTitle}</h2>
          </header>
          <div>
            {invitation.story.map((item, index) => (
              <article key={`${item.period}-${index}`} data-wedding-v2-reveal>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item.period}</p>
                <h3>{item.title}</h3>
                <small>{item.description}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="wedding-v2-gallery">
          <header data-wedding-v2-reveal>
            <span className="wedding-v2-label">Dalam bingkai</span>
            <h2>{invitation.galleryTitle}</h2>
            <p>Sentuh foto untuk melihat lebih dekat.</p>
          </header>
          <div className="wedding-v2-gallery-grid">
            {invitation.gallery.map((image, index) => (
              <button
                type="button"
                key={`${image.imageUrl}-${index}`}
                onClick={() => setLightbox(index)}
                data-wedding-v2-reveal
                aria-label={`Perbesar foto: ${image.altText}`}
              >
                <img src={image.imageUrl} alt={image.altText} loading="lazy" />
                <span>{String(index + 1).padStart(2, "0")}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="wedding-v2-closing">
          <div className="wedding-v2-closing-image" data-wedding-v2-reveal>
            <img
              src={invitation.gallery[3]?.imageUrl || invitation.heroImageUrl}
              alt={invitation.gallery[3]?.altText || "Cincin pernikahan"}
              loading="lazy"
            />
          </div>
          <div className="wedding-v2-closing-copy">
            <span data-wedding-v2-reveal>Terima kasih</span>
            <h2 data-wedding-v2-reveal>{invitation.closingTitle}</h2>
            <p data-wedding-v2-reveal>{invitation.closingMessage}</p>
            {whatsapp && (
              <a
                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(
                  `Halo ${invitation.brideName} dan ${invitation.groomName}, saya ${recipient} ingin mengonfirmasi kehadiran.`,
                )}`}
                target="_blank"
                rel="noreferrer"
                data-wedding-v2-reveal
              >
                Konfirmasi kehadiran <ArrowUpRight size={17} />
              </a>
            )}
            <strong data-wedding-v2-reveal>
              {invitation.brideName} <i>&amp;</i> {invitation.groomName}
            </strong>
          </div>
        </section>

        <footer className="wedding-footer wedding-v2-footer">
          <Heart size={13} fill="currentColor" />
          <span>Steria House</span>
          <i aria-hidden="true">•</i>
          <span>Undangan Digital</span>
        </footer>
      </div>

      {lightbox !== null && invitation.gallery[lightbox] && (
        <div
          className="wedding-lightbox wedding-v2-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Galeri foto pernikahan"
          onClick={() => setLightbox(null)}
        >
          <button type="button" onClick={() => setLightbox(null)} aria-label="Tutup galeri">
            <X size={22} />
          </button>
          <img
            src={invitation.gallery[lightbox].imageUrl}
            alt={invitation.gallery[lightbox].altText}
            onClick={(event) => event.stopPropagation()}
          />
          <span>
            {String(lightbox + 1).padStart(2, "0")} /{" "}
            {String(invitation.gallery.length).padStart(2, "0")}
          </span>
        </div>
      )}
    </main>
  );
}

export function WeddingInvitationPage({
  version = "v1",
}: {
  version?: "v1" | "v2";
}) {
  const [searchParams] = useSearchParams();
  const recipient = useMemo(
    () => cleanRecipient(searchParams.get("kepada")),
    [searchParams],
  );
  const query = useQuery({
    queryKey: ["wedding-invitation", version],
    queryFn: () => getWeddingInvitation(version),
    staleTime: 60_000,
  });

  useWeddingMetadata(`/wedding/${version}`, query.data?.heroImageUrl);

  const personalizedInvitation = query.data
    ? {
        ...query.data,
        groomName: cleanDisplayName(searchParams.get("m"), query.data.groomName),
        brideName: cleanDisplayName(searchParams.get("f"), query.data.brideName),
      }
    : null;

  if (query.isLoading) {
    return (
      <div className="wedding-loading">
        <LoadingState label="Menyiapkan undangan" />
      </div>
    );
  }
  if (query.isError || !personalizedInvitation) {
    return (
      <main className="wedding-error">
        <Heart size={28} />
        <h1>Undangan belum dapat dibuka</h1>
        <p>Silakan coba kembali dalam beberapa saat.</p>
        <button type="button" onClick={() => query.refetch()}>
          Muat ulang
        </button>
      </main>
    );
  }

  return version === "v2" ? (
    <InvitationContentV2 invitation={personalizedInvitation} recipient={recipient} />
  ) : (
    <InvitationContent invitation={personalizedInvitation} recipient={recipient} />
  );
}
